import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, onMounted } from "vue";
import { DateTime, Duration } from "luxon";
import { PetitionShiftSearch } from "@/models/petition/PetitionResponse";
import { Attendance } from "@/models/transaction/Attendance";
import { InputRules } from "@/rules";
import TimePicker2 from "@/components/TimePicker2.vue";
import {
  PulldownResponse,
  codeKbnFilter
} from "@/models/response/PulldownResponse";
import { CodeKbnList } from "@/models/codeMaster/Code";
import { Paths } from "@/plugins/api/paths";
import { $get, $post } from "@/plugins/api/api";
import { $alert } from "@/plugins/alert/alert";
import { $confirm } from "@/plugins/confirm/confirm";
import { WorkResultsApplicationRequest } from "@/models/transaction/AttendanceFlowDat";

export type WorkResults = {
  startTime?: string | null;
  endTime?: string | null;
  morningBreak?: string | null;
  lunchBreak?: string | null;
  midnightBreak?: string | null;
  opeTime?: string | null;
};
type WorkResultsKey = keyof WorkResults;

type Props = {
  shift?: PetitionShiftSearch;
  attendance?: Attendance;
  date: DateTime;
  affiliationId: number;
  employeeId: number;
};

interface VForm extends Vue {
  reset(): void;
  resetValidation(): void;
  validate(): boolean;
}

type Headers = {
  value: WorkResultsKey;
  text: string;
  color?: string;
  rules?: Array<boolean | string | Function>;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'ChangeWorkResultsApplication',
  props: {
    shift: null,
    attendance: null,
    date: null,
    affiliationId: null,
    employeeId: null
  },
  setup(__props: any, { expose }) {

const props = __props as {
  shift?: PetitionShiftSearch;
  attendance?: Attendance;
  date: DateTime;
  affiliationId: number;
  employeeId: number;
};

const Message = ["公共交通機関の遅延の場合は、遅延証明書を提出してください。"];

const Message2 = [
  "終了時間を現状届の終了時間よりも遅く設定することはできません。\n",
  "残業をした場合は、必ず残業申請を実施してください。\n",
  "※残業をした場合の休憩時間は、残業の休憩を含んで申請してください。"
];

const formatTime = (time: string | null | undefined) => {
  if (time) {
    const datetime = DateTime.fromISO(time);
    if (datetime.isValid) {
      const format = datetime.toFormat("HH:mm");
      if (format) {
        return format;
      }
    }
  }
  return undefined;
};

const initWorkResults = (value: WorkResults): WorkResults => {
  return {
    startTime: formatTime(value.startTime),
    endTime: formatTime(value.endTime),
    morningBreak: formatTime(value.morningBreak),
    lunchBreak: formatTime(value.lunchBreak),
    midnightBreak: formatTime(value.midnightBreak),
    opeTime: formatTime(value.opeTime)
  };
};



const form = ref<WorkResults>({});
const reason = ref<string[]>([]);
const vform = ref<VForm>();
const reasonList = ref<string[]>([]);

const startTime = ref<DateTime>();
const endTime = ref<DateTime>();
const shiftBreakTime = ref<Duration>();
const overBreakTime = ref<Duration>();

const breakTimeRule = (val: unknown) => {
  const from = typeof val === "string" ? Duration.fromISOTime(val) : undefined;

  const shift = shiftBreakTime.value;
  const overwork = overBreakTime.value;

  if (!shift?.isValid || !from?.isValid) return true;

  const to = overwork?.isValid ? shift.plus(overwork) : shift;

  const time = to.toISOTime({ suppressSeconds: true });
  const equalMessage = `${time}を入力してください。`;
  const greaterMessage = `${time}以内で入力してください。`;
  //const lessThanMessage = `${time}以上で入力してください。`;

  const equal = +from === +to || equalMessage;
  const greater = from <= to || greaterMessage;
  //const lessThan = from >= to || lessThanMessage;

  if (overwork?.isValid && overwork?.toMillis() == 86340000) {
    return true;
  } else if (overwork?.isValid && overwork?.toMillis() !== 0) {
    console.log(overwork?.toMillis(), "millis");
    return equal;
  } else if (overwork?.isValid && overwork?.toMillis() == 0) {
    console.log(overwork?.toMillis(), "millis");
    return greater;
  } else {
    return true;
  }
};

const endTimeRule = (val: unknown) => {
  const to = endTime.value;
  const from = typeof val === "string" ? DateTime.fromISO(val) : undefined;
  if (!from?.isValid || !to?.isValid) return true;
  const message = `超過は、残業申請してください。`;
  return from <= to || message;
};

const startTimeRule = (val: unknown) => {
  const today = props.date;
  console.log("today", today);
  if (today.year === 2024) {
    if (today.month < 9) {
      return true;
    } else if (today.month === 9) {
      if (today.day < 16) {
        return true;
      }
    }
  }
  const to = startTime.value;
  //console.log("stat", startTime.value,props.shift, props.shift?.lastStarttime, val);
  const from = typeof val === "string" ? DateTime.fromISO(val) : undefined;
  if (!from?.isValid || !to?.isValid) return true;
  //console.log(to.diff(from).milliseconds, "diff");
  // if (props.shift?.lastStarttime) {
  //   const lastStartTime = DateTime.fromISO(props.shift?.lastStarttime);
  //   const diffFromLastStartTime = lastStartTime.diff(from).milliseconds;
  //   const message = `早出は、30分前までです。`;
  //   return diffFromLastStartTime <= 1800000 || message;
  // }

  const message = `早出は、２時間前までです。`;
  const diff = to.diff(from).milliseconds;
  return diff <= 7200000 || message;
};

const header: Headers[] = [
  { text: "開始時間： ", value: "startTime", rules: [startTimeRule] },
  { text: "終了時間： ", value: "endTime", rules: [endTimeRule] },
  { text: "休憩総時間： ", value: "lunchBreak", rules: [breakTimeRule] },
  { text: "早朝休憩時間： ", value: "morningBreak" },
  { text: "深夜休憩時間： ", value: "midnightBreak" },
  { text: "OPE時間", value: "opeTime" }
];

const dispShift = computed(() =>
  initWorkResults({
    startTime: props.shift?.shiftPatternStartTime,
    endTime: props.shift?.shiftPatternEndTime,
    morningBreak: props.shift?.shiftPatternMorningBreakTime,
    lunchBreak: props.shift?.shiftPatternBreakTime,
    midnightBreak: props.shift?.shiftPatternMidnightBreakTime,
    opeTime: props.shift?.shiftPatternOpeTime
  })
);

const dispAttendance = computed(() =>
  initWorkResults({
    startTime: props.attendance?.startTime,
    endTime: props.attendance?.endTime,
    morningBreak: props.attendance?.morningBreakTime,
    lunchBreak: props.attendance?.breakTime,
    midnightBreak: props.attendance?.midnightBreakTime,
    opeTime: props.attendance?.opeTime
  })
);

const getCodeMaster = async () => {
  const code = await $get<PulldownResponse[]>(Paths.codeMasterPulldown);
  if (code) {
    reasonList.value = codeKbnFilter(code, CodeKbnList.ATTRSN).map(e => e.name);
  }
};
const initialize = () =>
  initWorkResults({
    startTime:
      props.attendance?.startTime || props.shift?.shiftPatternStartTime,
    endTime: props.attendance?.endTime || props.shift?.shiftPatternEndTime,
    lunchBreak:
      props.attendance?.breakTime || props.shift?.shiftPatternBreakTime,
    morningBreak:
      props.attendance?.morningBreakTime ||
      props.shift?.shiftPatternMorningBreakTime,
    midnightBreak:
      props.attendance?.midnightBreakTime ||
      props.shift?.shiftPatternMidnightBreakTime,
    opeTime: props.attendance?.opeTime || props.shift?.shiftPatternOpeTime
  });

const timeValidate = async () => {
  const message =
    "開始打刻よりも前の時間または、終了打刻よりも後ろの時間を入力されています。\n本当に、この内容で送信しても構いませんか？";
  const start = DateTime.fromSQL(form.value.startTime || "");
  const end = DateTime.fromSQL(form.value.endTime || "");
  const shiftStart = DateTime.fromSQL(props.shift?.shiftPatternStartTime || "");
  const shiftEnd = DateTime.fromSQL(props.shift?.shiftPatternEndTime || "");

  if (start.isValid && shiftStart.isValid) {
    if (start < shiftStart) return await $confirm(message);
  }
  if (end.isValid && shiftEnd.isValid) {
    if (end > shiftEnd) return await $confirm(message);
  }
  return true;
};

const getOverBreakTime = async (shiftId: string) => {
  return await $get<string>(
    `${Paths.approval.attendance.overBreakTime}/${shiftId}`
  );
};
const getShiftBreakTime = async (shiftId: string) => {
  return await $get<string>(
    `${Paths.approval.attendance.shiftBreakTime}/${shiftId}`
  );
};
const getEndTime = async (shiftId: string) => {
  return await $get<string>(`${Paths.approval.attendance.endTime}/${shiftId}`);
};
const getStartTime = async (shiftId: string) => {
  return await $get<string>(
    `${Paths.approval.attendance.startTime}/${shiftId}`
  );
};

watch(
  () => props.shift?.shiftId,
  async val => {
    if (val) {
      const obt = await getOverBreakTime(val);
      const sbt = await getShiftBreakTime(val);
      const et = await getEndTime(val);
      const st = await getStartTime(val);
      overBreakTime.value = obt ? Duration.fromISOTime(obt) : undefined;
      shiftBreakTime.value = sbt ? Duration.fromISOTime(sbt) : undefined;
      endTime.value = et ? DateTime.fromISO(et) : undefined;
      startTime.value = st ? DateTime.fromISO(st) : undefined;
    }
  },
  { immediate: true }
);

const onSubmit = async () => {
  if (!vform.value?.validate()) {
    $alert(
      "warning",
      "入力に誤りがあります。\n入力内容を確認してください。\n\n残業申請をしている場合は、\n休憩時間を合わせてください。"
    );
    return;
  }
  if (!(await timeValidate())) return;
  const request: WorkResultsApplicationRequest = {
    recordId: 0,
    requestDate: DateTime.local().toISO(),
    attendanceDate: props.date.toISO(),
    affiliationId: props.affiliationId,
    employeeId: props.employeeId,
    shiftId: props.shift?.shiftId,
    preStartTime: props.attendance?.startTime,
    preEndTime: props.attendance?.endTime,
    preMorningBreakTime: props.attendance?.morningBreakTime,
    preBreakTime: props.attendance?.breakTime,
    preMidnightBreakTime: props.attendance?.midnightBreakTime,
    preOpeTime: props.attendance?.opeTime,
    afterStartTime: form.value.startTime,
    afterEndTime: form.value.endTime,
    afterMorningBreakTime: form.value.morningBreak,
    afterBreakTime: form.value.lunchBreak,
    afterMidnightBreakTime: form.value.midnightBreak,
    afterOpeTime: form.value.opeTime,
    startTimeStamp: props.attendance?.startTimeUnix
      ? DateTime.fromSeconds(props.attendance?.startTimeUnix).toFormat("HH:mm")
      : undefined,
    endTimeStamp: props.attendance?.endTimeUnix
      ? DateTime.fromSeconds(props.attendance?.endTimeUnix).toFormat("HH:mm")
      : undefined,
    reasons: reason.value
  };

  const result = await $post(Paths.petition.insert.attendance, request);
  if (result) {
    $alert(
      "success",
      "申請に成功しました。\n残業をしている場合は、残業申請も実施してください。"
    );
    return true;
  } else {
    $alert("error", "申請に失敗しました。");
    return false;
  }
};

onMounted(async () => {
  await getCodeMaster();
  form.value = initialize();
});

expose({ onSubmit });

return { __sfc: true,Message, Message2, formatTime, initWorkResults, props, form, reason, vform, reasonList, startTime, endTime, shiftBreakTime, overBreakTime, breakTimeRule, endTimeRule, startTimeRule, header, dispShift, dispAttendance, getCodeMaster, initialize, timeValidate, getOverBreakTime, getShiftBreakTime, getEndTime, getStartTime, onSubmit, InputRules, TimePicker2 }
}

})